import {
  Button,
  Center,
  Divider,
  Flex,
  Group,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import useRetrieveAutomationRuns from '@/hooks/api/runs/useRetrieveAutomationRuns.ts';
import { useMemo, useState } from 'react';
import { flattenInfiniteQueryData } from '@/utils/misc.ts';
import { useDebouncedValue } from '@mantine/hooks';
import { SHORT_DEBOUNCE_TIME } from '@/utils/constants.ts';
import CustomSpinner from '@/components/Spinner.tsx';
import RunHistoryCard from '@/pages/Run/components/RunHistorySidebar/RunHistoryCard.tsx';
import FilterSelect from '@/pages/Run/components/RunHistorySidebar/FilterSelect.tsx';
import TextSearchBox from '@/components/TextSearchBox.tsx';
import {
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineMagnifyingGlass,
} from 'react-icons/hi2';
import AppIcon from '@/components/AppIcon.tsx';
import RunHistoryManualRunButton from '@/pages/AutomationPage/components/ManualRunModal/components/RunHistoryManualRunButton.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import type { Automation } from '@/types/automations.ts';
import useRunFilters from '@/hooks/useRunFilters.ts';

interface RunHistorySidebarProps {
  automation: Automation;
  width: number;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

const COLLAPSED_SIDEBAR_WIDTH = 40;
const HEADER_ICON_BUTTON_STYLES = {
  root: {
    padding: '5px',
    width: '25px',
    height: '25px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

export function RunHistorySidebar({
  automation,
  width,
  collapsed,
  onCollapsedChange,
}: RunHistorySidebarProps) {
  const [showSearch, setShowSearch] = useState(false);
  const {
    filterStatuses,
    filterRunOrigins,
    filterRunModes,
    filterProvenanceStates,
    toggleRunMode,
    toggleStatus,
    toggleRunOrigin,
    toggleProvenanceState,
    onlyRunOrigin,
    onlyStatus,
    onlyRunMode,
    onlyProvenanceState,
    resetFilters,
    originOptions,
    modeOptions,
    provenanceOptions,
    statusOptions,
  } = useRunFilters();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, SHORT_DEBOUNCE_TIME);

  const {
    data: runs,
    isPending: isLoadingRuns,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useRetrieveAutomationRuns({
    statuses: filterStatuses,
    search: debouncedSearchQuery,
    runModes: filterRunModes,
    runOrigins: filterRunOrigins,
    provenanceStates: filterProvenanceStates,
  });

  const allRuns = useMemo(() => flattenInfiniteQueryData(runs), [runs]);

  const totalRuns = runs?.pages?.[0]?.count ?? 0;
  const toggleLabel = collapsed ? 'Expand run history' : 'Collapse run history';

  return (
    <Stack
      w={collapsed ? COLLAPSED_SIDEBAR_WIDTH : width}
      style={{
        borderRight: collapsed ? '1px solid #e0e0e0' : undefined,
        flexShrink: 0,
        overflow: 'hidden',
      }}
      gap={'0'}
    >
      <Flex
        justify={collapsed ? 'center' : 'space-between'}
        align="center"
        pl={collapsed ? 0 : 'md'}
        pr={collapsed ? 0 : 'sm'}
        // style={{ borderBottom: '1px solid #e0e0e0' }}
        h={50}
        mih={'50px'}
        wrap={'nowrap'}
      >
        {!collapsed && (
          <Text fw={500} size={'sm'}>
            Runs {totalRuns > 0 ? `(${totalRuns})` : ''}
          </Text>
        )}
        <Group gap={0}>
          <Tooltip label={toggleLabel} position="right" openDelay={250}>
            <StyledButton
              variant="subtleIcon"
              size="sm"
              aria-label={toggleLabel}
              aria-expanded={!collapsed}
              onClick={() => onCollapsedChange(!collapsed)}
              styles={HEADER_ICON_BUTTON_STYLES}
            >
              <AppIcon
                Icon={collapsed ? HiOutlineChevronRight : HiOutlineChevronLeft}
                size={16}
                iconProps={{ strokeWidth: 2 }}
              />
            </StyledButton>
          </Tooltip>
          {!collapsed && (
            <>
              <StyledButton
                variant={'subtleIcon'}
                size={'sm'}
                aria-label="Search runs"
                onClick={() => {
                  setShowSearch(!showSearch);
                  setSearchQuery('');
                }}
                styles={HEADER_ICON_BUTTON_STYLES}
              >
                <AppIcon Icon={HiOutlineMagnifyingGlass} size={16} iconProps={{ strokeWidth: 2 }} />
              </StyledButton>
              <FilterSelect
                statuses={filterStatuses}
                toggleStatus={toggleStatus}
                onlyStatus={onlyStatus}
                statusOptions={statusOptions}
                runModes={filterRunModes}
                toggleRunMode={toggleRunMode}
                onlyRunMode={onlyRunMode}
                modeOptions={modeOptions}
                runOrigins={filterRunOrigins}
                toggleRunOrigin={toggleRunOrigin}
                onlyRunOrigin={onlyRunOrigin}
                originOptions={originOptions}
                provenanceStates={filterProvenanceStates}
                toggleProvenanceState={toggleProvenanceState}
                onlyProvenanceState={onlyProvenanceState}
                provenanceOptions={provenanceOptions}
                resetFilters={resetFilters}
              />
            </>
          )}
        </Group>
      </Flex>

      {!collapsed && showSearch && (
        <Flex p={'xs'} pt={0}>
          <TextSearchBox
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            placeholder={'Search Runs'}
            width={'100%'}
          />
        </Flex>
      )}
      {!collapsed && (
        <>
          <Divider mt="0" mb={'0'} />

          <RunHistoryManualRunButton automation={automation} />

          <ScrollArea offsetScrollbars={'y'} pt={'xs'}>
            {/*<Flex style={{ width: '100%', height: '100%', overflowY: 'auto', position: 'relative' }}>*/}
            <Flex pl={'12px'} pb={'md'} pr={0} w={'100%'} direction={'column'}>
              {isLoadingRuns ? (
                <CustomSpinner />
              ) : (
                <Stack gap={'2px'} w={'100%'}>
                  {allRuns.map((run) => (
                    <RunHistoryCard key={run.id} run={run} />
                  ))}
                  {allRuns.length === 0 && (
                    <Center mt={'xs'}>
                      <Text size={'xs'} c={'#757575'}>
                        No runs found
                      </Text>
                    </Center>
                  )}
                </Stack>
              )}
              {hasNextPage && (
                <Group align={'center'} w={'100%'} justify={'center'}>
                  <Button loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
                    Load More
                  </Button>
                </Group>
              )}
            </Flex>
            {/*</Flex>*/}
          </ScrollArea>
        </>
      )}
    </Stack>
  );
}
